import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, first, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { unsubscribeAll } from '../../../common/app.helpers';
import {
  DoctorPreferences,
  PatientDistribution,
  PatientDistributionFilter,
  RelationGeography,
  RelationGeographyInner,
  DefaultService as RestApiService,
} from '../../../swagger-api';
import { MaybeNull, MaybeUndefined } from '../../../utils';
import { PatientLinkRequestService } from '../../user-relation-requests/services/patient-link-request.service';
import { calculateRelationsCount } from '../../user-relation-requests/services/relation-shared.service';

interface DistributionResult {
  preferences: DoctorPreferences;
  relationGeography: RelationGeography;
}
export interface DistributionPreferencesData {
  relationGeography: RelationGeography;
  total: number;
  countriesList: string[];
  selectedCountry?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DistributionPreferencesService {
  private readonly preferencesData$$: Subject<DistributionPreferencesData> = new Subject<DistributionPreferencesData>();
  private readonly apiService = inject(RestApiService);
  private readonly patientLinkService = inject(PatientLinkRequestService);

  private readonly subscriptions$: Subscription[] = [];

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public get preferencesData(): Observable<DistributionPreferencesData> {
    return this.preferencesData$$.asObservable();
  }

  public updateDistributionPreferences(preferences: DoctorPreferences, userId: string): void {
    this.subscriptions$.push(
      this.apiService
        .restMedicalDoctorPreferencesPut(preferences, userId)
        .pipe(
          first(),
          catchError(() => EMPTY),
        )
        .subscribe(),
    );
  }

  public getDistributionResult(userId: string, forSelf: boolean, preferences?: DoctorPreferences): void {
    let preferences$: Observable<DoctorPreferences>;

    if (!forSelf) {
      preferences$ = preferences ? of(preferences) : of({});
    } else {
      preferences$ = this.getDoctorPreferences(userId);
    }

    this.subscriptions$.push(
      preferences$
        .pipe(
          switchMap((preferences: DoctorPreferences): Observable<DistributionResult> => {
            return this.patientLinkService.getRelationGeography(userId).pipe(
              catchError(() => EMPTY),
              map((relationGeography: RelationGeography) => {
                return {
                  preferences,
                  relationGeography,
                };
              }),
            );
          }),
          switchMap((distributionResult: DistributionResult): Observable<DistributionPreferencesData> => {
            const patientDistribution: MaybeNull<PatientDistribution> =
              distributionResult.preferences?.display_options?.patient_distribution;

            let selectedCountry: MaybeUndefined<string> = undefined;

            if (patientDistribution) {
              selectedCountry = patientDistribution.value;
            } else if (distributionResult.relationGeography.length == 1) {
              const geographyData: RelationGeographyInner = distributionResult.relationGeography[0];
              selectedCountry = geographyData.name;
            } else {
              return of({
                relationGeography: distributionResult.relationGeography,
                total: calculateRelationsCount(distributionResult.relationGeography),
                countriesList: this.extractRelationName(distributionResult.relationGeography),
              });
            }

            return this.patientLinkService
              .getRelationGeography(userId, PatientDistributionFilter.Country, selectedCountry)
              .pipe(
                catchError(() => EMPTY),
                map((relationGeography: RelationGeography) => {
                  return {
                    relationGeography: relationGeography,
                    total: calculateRelationsCount(distributionResult.relationGeography),
                    countriesList: this.extractRelationName(distributionResult.relationGeography),
                    selectedCountry: selectedCountry,
                  };
                }),
              );
          }),
        )
        .subscribe((preferencesData: DistributionPreferencesData) => {
          this.preferencesData$$.next(preferencesData);
        }),
    );
  }

  private getDoctorPreferences(userId: string): Observable<DoctorPreferences> {
    return this.apiService.restMedicalDoctorPreferencesGet(userId).pipe(catchError(() => EMPTY));
  }

  private extractRelationName(relationGeography: RelationGeography): string[] {
    return relationGeography.map((relation: RelationGeographyInner) => relation.name);
  }
}
